import { useState, useSyncExternalStore } from 'react'
import { Alert, Button, Card, Form } from 'react-bootstrap'
import { useAppStore } from '../stores/appStore'
import { useTaskRuntime } from '../contexts/TaskRuntimeContext'
import { API_TRANSPORT, canAutoRecover, getProxySettings, subscribeProxySettings, updateProxySettings } from '../services/proxySettings.js'
import { ProxyTransport } from '../services/proxyTransport.js'

export default function ProxyAccess() {
  const settings = useSyncExternalStore(subscribeProxySettings, getProxySettings, getProxySettings)
  const [key, setKey] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState(null)
  const { resumeProxyExecution, stopProxyRecovery } = useTaskRuntime()
  if (API_TRANSPORT !== 'proxy') return null

  const save = () => {
    if (!key.trim()) { setMessage({ variant: 'warning', text: '请输入站点访问口令，与游戏账号密码不同。' }); return }
    try {
      updateProxySettings({ accessKey: key.trim(), paused: true, code: 'KEY_CHANGED', reason: '访问口令已更新，请验证后手动恢复' })
      setKey('')
      setMessage({ variant: 'success', text: '口令已保存在当前浏览器，请验证访问后手动恢复执行。' })
    } catch { setMessage({ variant: 'danger', text: '浏览器拒绝保存设置，口令未更新。' }) }
  }
  const perform = async resume => {
    setBusy(true)
    setMessage(null)
    try {
      if (resume) {
        if (!await resumeProxyExecution()) return
      } else await (useAppStore.getState().apiService?.transport || new ProxyTransport()).verifyAccessKey()
      setMessage({ variant: 'success', text: resume ? '游戏连接已恢复，正在继续任务和定时调度。' : '访问口令验证成功；恢复执行时还会检查游戏服务器连通性。' })
    } catch (error) { setMessage({ variant: 'danger', text: error.message }) }
    finally { setBusy(false) }
  }
  const clear = () => {
    try {
      updateProxySettings({ accessKey: '', paused: true, code: 'KEY_CLEARED', reason: '访问口令已清除，执行保持暂停' })
      setKey('')
      setMessage({ variant: 'success', text: '已清除保存的访问口令。' })
    } catch { setMessage({ variant: 'danger', text: '浏览器拒绝更新设置，口令未清除。' }) }
  }
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(settings.accessKey)
      setMessage({ variant: 'success', text: '访问口令已复制，请仅分享给需要使用本站的熟人。' })
    } catch { setMessage({ variant: 'danger', text: '浏览器拒绝复制口令，请检查剪贴板权限。' }) }
  }
  return (
    <Card className="mb-4">
      <Card.Header><h5 className="mb-0">访问口令</h5></Card.Header>
      <Card.Body>
        <p className="text-muted">使用站点维护者提供的访问口令，与游戏账号密码不同。只保存在当前浏览器，不会进入迁移备份。</p>
        <Form onSubmit={event => { event.preventDefault(); save() }}>
          <Form.Label htmlFor="proxy-access-key">站点访问口令</Form.Label>
          <Form.Control id="proxy-access-key" type="password" autoComplete="off" value={key}
            placeholder={settings.accessKey ? '已保存口令；输入可更换' : '输入访问口令'} disabled={busy}
            onChange={event => setKey(event.target.value)} style={{ maxWidth: 480 }} />
          <div className="d-flex gap-2 flex-wrap mt-3">
            <Button type="submit" disabled={busy || !key.trim()}>保存口令</Button>
            <Button variant="outline-primary" onClick={() => perform(false)} disabled={busy || !settings.accessKey}>验证访问</Button>
            <Button variant="outline-success" onClick={() => perform(true)} disabled={busy || !settings.accessKey || !settings.paused}>恢复执行 / 调度</Button>
            {canAutoRecover(settings) && <Button variant="outline-warning" onClick={stopProxyRecovery}>停止自动恢复</Button>}
            <Button variant="outline-secondary" onClick={copy} disabled={busy || !settings.accessKey}>复制已保存口令</Button>
            <Button variant="outline-danger" onClick={clear} disabled={busy || !settings.accessKey}>清除口令</Button>
          </div>
        </Form>
        {settings.paused && <Alert variant="warning" className="mt-3 mb-0">
          {canAutoRecover(settings) ? <>连接中断，自动重试中：{settings.reason}。
            {settings.recovering ? '正在检查代理和游戏连接…' : settings.nextRetryAt ? `下次重试：${new Date(settings.nextRetryAt).toLocaleTimeString()}。` : '等待自动重试…'}
            连接恢复后将重新执行中断任务，继续剩余队列与定时调度。</> : <>执行已暂停：{settings.reason || '请验证口令后手动恢复'}。</>}
        </Alert>}
        {!settings.paused && settings.lastRecoveredAt > 0 && <Alert variant="success" className="mt-3 mb-0" role="status">
          游戏连接已于 {new Date(settings.lastRecoveredAt).toLocaleTimeString()} 恢复，任务与定时调度已继续。
        </Alert>}
        {message && <Alert variant={message.variant} className="mt-3 mb-0" role="status">{message.text}</Alert>}
      </Card.Body>
    </Card>
  )
}
